import React, { useEffect, useState } from "react";
import {
  Table, Spinner, Alert, Input, Button, Form, FormGroup, Label,
} from "reactstrap";
import axiosInstance from "../../Request";
import { URL } from "../../api";

const CustomerReporting = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState("");

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [customerId, setCustomerId] = useState("");
  const [panNumber, setPanNumber] = useState("");
  const [filterType, setFilterType] = useState("customerId");

  const [currentPage, setCurrentPage] = useState(1);
  const rowsPerPage = 30;
  const [hasNextPage, setHasNextPage] = useState(false);
  const [activeFilters, setActiveFilters] = useState({});

  useEffect(() => {
    fetchCustomerData(1, {});
  }, []);

  const fetchCustomerData = async (page = 1, filters = {}) => {
    setLoading(true);
    setError("");
    try {
      const params = { ...filters, page: page - 1, size: rowsPerPage };
      const res = await axiosInstance.get(URL.REPORTING_CUSTOMER_SUMMARY, { params });
      const resultData = Array.isArray(res.data) ? res.data : [];
      setData(resultData);
      setCurrentPage(page);
      setHasNextPage(resultData.length === rowsPerPage);
    } catch (err) {
      setError("Failed to fetch customer data.");
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    if (e) e.preventDefault();
    const filters = {};
    if (fromDate) filters.fromDate = fromDate;
    if (toDate) filters.toDate = toDate;
    if (filterType === "customerId" && customerId) filters.customerId = customerId;
    if (filterType === "panNumber" && panNumber) filters.panNumber = panNumber;
    setActiveFilters(filters);
    fetchCustomerData(1, filters);
  };

  // SCALABLE EXCEL DOWNLOAD LOGIC
  const handleExport = async () => {
    if (data.length === 0) return alert("No data available to export!");
    setExporting(true);
    try {
      const response = await axiosInstance.get(URL.REPORTING_CUSTOMER_SUMMARY, {
        params: { ...activeFilters, export: true },
        responseType: 'blob', // Critical for binary file handling
      });

      // Use the specific MIME type for Excel CSV compatibility
      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Professional timestamped filename
      const timestamp = new Date().toISOString().split('T')[0];
      link.setAttribute('download', `Customer_Summary_${timestamp}.csv`);
      
      document.body.appendChild(link);
      link.click();
      
      // Clean up to prevent memory leaks
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      alert("Export failed. Please check your session.");
    } finally {
      setExporting(false);
    }
  };

  const font10 = { fontSize: "10px" };
  const inputSmStyle = { fontSize: "11px", height: "30px", padding: "2px 6px" };
  const buttonSmStyle = { fontSize: "11px", padding: "4px 10px", height: "30px", lineHeight: "1" };

  return (
    <div style={font10}>
      <Form onSubmit={handleSearch} className="mb-3">
        <div className="d-flex align-items-end flex-wrap" style={{ gap: "10px" }}>
          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">Filter Type</Label>
            <Input type="select" bsSize="sm" value={filterType} onChange={(e) => setFilterType(e.target.value)} style={inputSmStyle}>
              <option value="customerId">Customer ID</option>
              <option value="panNumber">PAN Number</option>
            </Input>
          </FormGroup>
          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">{filterType === "customerId" ? "Customer ID" : "PAN Number"}</Label>
            <Input type={filterType === "customerId" ? "number" : "text"} bsSize="sm" value={filterType === "customerId" ? customerId : panNumber} onChange={(e) => filterType === "customerId" ? setCustomerId(e.target.value) : setPanNumber(e.target.value)} style={inputSmStyle} />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">From Date</Label>
            <Input type="date" bsSize="sm" value={fromDate} onChange={(e) => setFromDate(e.target.value)} style={inputSmStyle} />
          </FormGroup>
          <FormGroup className="mb-0">
            <Label className="mb-1 fw-bold">To Date</Label>
            <Input type="date" bsSize="sm" value={toDate} onChange={(e) => setToDate(e.target.value)} style={inputSmStyle} />
          </FormGroup>
          <div className="d-flex" style={{ gap: "6px" }}>
            <Button color="primary" type="submit" style={buttonSmStyle}>Search</Button>
            <Button color="success" type="button" onClick={handleExport} style={buttonSmStyle} disabled={exporting}>
              {exporting ? <Spinner size="sm" /> : "Excel Export"}
            </Button>
          </div>
        </div>
      </Form>

      {loading ? (
        <div className="text-center p-4"><Spinner size="sm" /> Loading...</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <Table bordered striped hover responsive size="sm" className="mt-3 mb-0">
            <thead className="thead-light">
              <tr className="text-nowrap text-center">
                <th rowSpan="2">#</th>
                <th rowSpan="2">Cust ID</th>
                <th rowSpan="2">Borrower Name</th>
                <th rowSpan="2">Fintech Name</th>
                <th colSpan="3" style={{ backgroundColor: "#e3f2fd" }}>Current Address</th>
                <th colSpan="3" style={{ backgroundColor: "#f1f8e9" }}>Office Address</th>
                <th colSpan="3" style={{ backgroundColor: "#fff3e0" }}>Permanent Address</th>
                <th rowSpan="2">Company Name</th>
                <th rowSpan="2">Income</th>
                <th rowSpan="2">Emp Type</th>
                <th rowSpan="2">PAN</th>
                <th rowSpan="2">Reg. Date</th>
              </tr>
              <tr className="text-nowrap text-center">
                <th style={{ backgroundColor: "#e3f2fd" }}>Address</th>
                <th style={{ backgroundColor: "#e3f2fd" }}>City</th>
                <th style={{ backgroundColor: "#e3f2fd" }}>State</th>
                <th style={{ backgroundColor: "#f1f8e9" }}>Address</th>
                <th style={{ backgroundColor: "#f1f8e9" }}>City</th>
                <th style={{ backgroundColor: "#f1f8e9" }}>State</th>
                <th style={{ backgroundColor: "#fff3e0" }}>Address</th>
                <th style={{ backgroundColor: "#fff3e0" }}>City</th>
                <th style={{ backgroundColor: "#fff3e0" }}>State</th>
              </tr>
            </thead>
            <tbody>
              {data.map((row, idx) => (
                <tr key={idx}>
                  <td>{(currentPage - 1) * rowsPerPage + idx + 1}</td>
                  <td>{row.customer_id}</td>
                  <td className="text-nowrap">{row.borrower_name}</td>
                  <td>{row.fintech_name || "N/A"}</td>
                  <td style={{ minWidth: "150px", whiteSpace: "normal" }}>{row.current_address || "-"}</td>
                  <td>{row.current_city || "-"}</td>
                  <td>{row.current_state || "-"}</td>
                  <td style={{ minWidth: "150px", whiteSpace: "normal" }}>{row.office_address || "-"}</td>
                  <td>{row.office_city || "-"}</td>
                  <td>{row.office_state || "-"}</td>
                  <td style={{ minWidth: "150px", whiteSpace: "normal" }}>{row.permanent_address || "-"}</td>
                  <td>{row.permanent_city || "-"}</td>
                  <td>{row.permanent_state || "-"}</td>
                  <td>{row.company_name || "-"}</td>
                  <td>{row.annual_income ? `₹${Number(row.annual_income).toLocaleString()}` : "-"}</td>
                  <td>{row.employment_type || "-"}</td>
                  <td>{row.pan_number || "-"}</td>
                  <td className="text-nowrap">{row.create_date}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      )}
      {/* Pagination remains the same */}
    </div>
  );
};

export default CustomerReporting;